import { z } from "zod";
import { botConfigV1Schema } from "./v1.js";

/**
 * `bot_versions.config`, schema version 2.
 *
 * Adds the two decisions a knowledge source creates for a bot: whether the
 * site brief goes in its prompt, and which sources it may read. Both live in
 * the versioned config rather than on the source, because they are per-bot —
 * one org can run a support bot that should know everything the site says
 * and a campaign bot that should only know one section.
 */
export const botConfigV2Schema = botConfigV1Schema.extend({
  /**
   * Whether the org's site brief is composed into this bot's system prompt.
   *
   * Defaults on: a bot that has a brief and doesn't use it is the setup
   * nobody would choose deliberately, and the brief is visible and editable
   * in the panel, so "on with a way to see it" beats "off until discovered".
   */
  useSiteBrief: z.boolean().default(true),
  /** Which knowledge sources this bot may search. Empty means every source the org has. */
  knowledgeSourceIds: z.array(z.string().uuid()).default([]),
});

export type BotConfigV2 = z.infer<typeof botConfigV2Schema>;

/**
 * v1 → v2. Both fields take their defaults, which is the behaviour a v1 bot
 * already had: no source configured means no brief and nothing to search, so
 * an old row loads with the same behaviour it was published with.
 */
export function migrateV1ToV2(raw: unknown): unknown {
  const source = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  return { ...source, useSiteBrief: source.useSiteBrief ?? true, knowledgeSourceIds: source.knowledgeSourceIds ?? [] };
}
